import { AnyFn } from '@direct-frontend/stdlib';
import { Subscribe, DataSourceBrand } from '../types';
import { currentReactionDataSources, getIsDataSourceCollecting } from '../currentReactionDataSources';

interface DataSourceConfig<F extends AnyFn> {
    createSubscribe: (value: unknown, ...params: Parameters<F>) => Subscribe | undefined;
    getValue: F;
    getValueInReaction?: F;
}

/**
 * Функция для создания источников.
 * Использует переменную currentReactionDataSources для сохранения используемых в реакции источников
 *
 * @typeParam F - функция получения данных источника
 * @param dataSourceConfig - конфигурация источника
 * @param dataSourceConfig.getValue - функция-декоратор над функцией получения данных источника.
 * В момент вызова создает функцию для подписки с переданными аргументами
 * @param [dataSourceConfig.getValueInReaction] - функция-декоратор аналогичная getValue.
 * Если передана, вызывается в момент вызова источника в реакции вместо getValue
 * @param dataSourceConfig.createSubscribe - функция для создания подписки на источник. Если возвращает undefined, подписка не создается
 * @returns декорированную функцию переданную в getValue
 */
export function createDataSource<F extends AnyFn>({
    createSubscribe,
    getValue,
    getValueInReaction = getValue,
}: DataSourceConfig<F>): F & DataSourceBrand {
    const dataSource = (...args: Parameters<F>) => {
        if (!getIsDataSourceCollecting()) {
            return getValue(...args);
        }

        let returnValue: unknown;

        /**
         * Если getValue выкинет ошибку, все равно создаем подписку
         */
        try {
            returnValue = getValueInReaction(...args);
        } catch (e) {
            /**
             * Записываем ссылку на новый объект.
             * Она будет использоваться для сравнения с предыдущим результатом и не будет ничему равна
             */
            returnValue = {};
            throw e;
        } finally {
            const subscribe = createSubscribe(returnValue, ...args);
            if (subscribe) {
                currentReactionDataSources.add(subscribe);
            }
        }

        return returnValue;
    };

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- добавляем DataSourceBrand
    return dataSource as F & DataSourceBrand;
}
